import './AssignToClientModal.scss';

import { Checkbox, DatePicker, Modal, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { FC, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { ReactComponent as SearchIcon } from 'src/assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ReactComponent as DownArrowIcon } from 'src/assets/images/onboarding-trainer/dashboard/program-generator-ai/components/down-arrow.svg';
import { ReactComponent as MonthIcon } from 'src/assets/images/onboarding-trainer/dashboard/program-generator-ai/components/month.svg';
import { ReactComponent as RadioTickIcon } from 'src/assets/images/onboarding-trainer/dashboard/program-generator-ai/components/radio-tick-2.svg';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/programs/close-icon.svg';
import { ReactComponent as WarningIcon } from 'src/assets/images/onboarding-trainer/solar_danger-triangle-linear.svg';
import {
  fetchTrainerProgramPlanList,
  FetchedProgramPlan,
} from 'src/api/mutations/useGetTrainerProgramPlanList';
import { sessionStore } from 'src/store/sessionStore';
import { useDeleteProgramPlan } from 'src/api/mutations/useDeleteProgramPlan';

interface AssignToClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (data: {
    clientId: string;
    startDate: string;
    notify: boolean;
    message: string;
  }) => Promise<void>;
}

export const AssignToClientModal: FC<AssignToClientModalProps> = ({
  isOpen,
  onClose,
  onAssign,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [clientPrograms, setClientPrograms] = useState<FetchedProgramPlan[]>([]);
  const [isCheckingPrograms, setIsCheckingPrograms] = useState(false);

  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid;
  const { mutateAsync: deleteProgram } = useDeleteProgramPlan();

  const { data, isLoading } = useGetTrainerClients(
    'active',
    debouncedSearch,
    'newest',
    currentPage,
    10,
  );
  const totalCount = data?.totalCount || 0;

  const prevSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    const filterChanged = prevSearchRef.current !== debouncedSearch;

    if (filterChanged) {
      setCurrentPage(1);
      prevSearchRef.current = debouncedSearch;
    }

    const fetchedClients = data?.clients;
    if (fetchedClients && fetchedClients.length > 0) {
      if (currentPage === 1 || filterChanged) {
        setAllClients(fetchedClients);
      } else {
        setAllClients((prev) => {
          const existingIds = new Set(prev.map((c) => c.uid));
          const newClients = fetchedClients.filter((c: any) => !existingIds.has(c.uid));
          return [...prev, ...newClients];
        });
      }
    } else if (
      fetchedClients &&
      fetchedClients.length === 0 &&
      (currentPage === 1 || filterChanged)
    ) {
      setAllClients([]);
    } else if (!fetchedClients && filterChanged) {
      setAllClients([]);
    }
  }, [data?.clients, currentPage, debouncedSearch]);

  useEffect(() => {
    if (selectedClient && trainerId) {
      setIsCheckingPrograms(true);
      fetchTrainerProgramPlanList(trainerId as string, {
        clientId: selectedClient.uid,
        isActive: '1',
      })
        .then((programs) => {
          const activeClientPrograms = (programs || []).filter(
            (p) => p.clientId === selectedClient.uid && String(p.isActive) === '1',
          );
          setClientPrograms(activeClientPrograms);
        })
        .catch(console.error)
        .finally(() => setIsCheckingPrograms(false));
    } else {
      setClientPrograms([]);
    }
  }, [selectedClient, trainerId]);

  const getInitials = (name: string, surname: string) => {
    const first = name ? name.charAt(0).toUpperCase() : '';
    const second = surname ? surname.charAt(0).toUpperCase() : '';
    return `${first}${second}`;
  };

  const handleAssign = async () => {
    if (!selectedClient) return;
    setIsAssigning(true);
    try {
      if (clientPrograms.length > 0) {
        await Promise.all(
          clientPrograms.map((p) =>
            deleteProgram({ docPath: p.docPath, hideToast: true }).catch(console.error),
          ),
        );
      }
      await onAssign({
        clientId: selectedClient.uid,
        startDate: startDate,
        notify,
        message: message.trim(),
      });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsAssigning(false);
    }
  };

  // reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setDebouncedSearch('');
      setSelectedClient(null);
      setStartDate(new Date().toISOString().split('T')[0]);
      setNotify(true);
      setMessage('');
      setCurrentPage(1);
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={540}
      closeIcon={null}
      rootClassName="assign-client-modal"
      className="logout-modal"
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(6, 7, 35, 0.8)',
        },
      }}
    >
      <div className="assign-client-content">
        <div className="modal-header">
          <h2 className="modal-title">Assign to a client</h2>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <p className="modal-subtitle">Choose a client and assign this workout</p>

        <div className="search-box">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search client"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={isAssigning}
          />
          {searchQuery && (
            <button
              className="clear-search-btn"
              aria-label="Clear search"
              onClick={() => setSearchQuery('')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>

        <div className="client-list-wrapper">
          <div
            className={`client-list-container ${allClients.length > 2 ? 'has-scroll' : ''}`}
            id="assignClientList"
          >
            {isLoading && allClients.length === 0 && (
              <div className="loading-state">
                <Spin size="small" /> <span style={{ marginLeft: 8 }}>Loading clients...</span>
              </div>
            )}
            {!isLoading && allClients.length === 0 && (
              <div className="empty-state">No clients found.</div>
            )}
            <InfiniteScroll
              dataLength={allClients.length}
              next={() => setCurrentPage((prev) => prev + 1)}
              hasMore={allClients.length < totalCount}
              loader={
                allClients.length > 0 ? (
                  <div className="loading-state">
                    <Spin size="small" />{' '}
                    <span style={{ marginLeft: 8 }}>Loading more clients...</span>
                  </div>
                ) : null
              }
              scrollableTarget="assignClientList"
            >
              {allClients.map((client) => {
                const isSelected = selectedClient?.uid === client.uid;
                return (
                  <div
                    key={client.uid}
                    className={`client-item ${isSelected ? 'selected' : ''} ${isAssigning ? 'disabled' : ''}`}
                    onClick={() => {
                      if (!isAssigning) setSelectedClient(client);
                    }}
                    style={{
                      opacity: isAssigning ? 0.5 : 1,
                      pointerEvents: isAssigning ? 'none' : 'auto',
                    }}
                  >
                    <div className="client-info">
                      <div className="client-avatar">
                        {client.avatarUrl ? (
                          <img src={client.avatarUrl} alt={client.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {getInitials(client.name, client.surname)}
                          </div>
                        )}
                      </div>
                      <span className="client-name">
                        {client.name} {client.surname}
                      </span>
                    </div>
                    <div className="client-radio">
                      {isSelected ? <RadioTickIcon /> : <div className="radio-circle"></div>}
                    </div>
                  </div>
                );
              })}
            </InfiniteScroll>
          </div>
          <div className="list-fade-overlay" />
        </div>

        {selectedClient && clientPrograms.length > 0 && !isCheckingPrograms && (
          <div
            className="inline-warning"
            style={{
              backgroundColor: '#2B2B2B',
              padding: 12,
              borderRadius: 8,
              marginTop: 16,
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
            }}
          >
            <WarningIcon
              style={{
                width: 24,
                height: 24,
                minWidth: 24,
                minHeight: 24,
                flexShrink: 0,
                marginTop: 2,
              }}
            />
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: '#FFFFFF' }}>
                Replace current program?
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#A0A0A0' }}>
                This client already has an active program. Assigning a new one will replace it, and
                any progress in the current program will be lost.
              </p>
            </div>
          </div>
        )}

        <div className="config-row" style={{ marginTop: 16 }}>
          <div className="config-group start-date-group">
            <label>Start date</label>
            <DatePicker
              format={(value) => {
                if (!value) return '';
                if (value.isSame(dayjs(), 'day')) {
                  return 'Today';
                }
                return value.format('D MMM, YYYY');
              }}
              value={startDate ? dayjs(startDate, 'YYYY-MM-DD') : null}
              onChange={(date) => setStartDate(date ? date.format('YYYY-MM-DD') : '')}
              disabledDate={(current) => {
                const today = dayjs().startOf('day');
                const maxDate = today.add(1, 'year');
                return current && (current < today || current > maxDate);
              }}
              allowClear={false}
              suffixIcon={<DownArrowIcon />}
              prefix={<MonthIcon />}
              className="dark-datepicker-custom"
              classNames={{ popup: 'dark-datepicker-popup' }}
              size="small"
              disabled={isAssigning}
            />
          </div>
          <div className="config-group notify-group">
            <div
              className={`notify-card ${notify ? 'active' : ''} ${isAssigning ? 'disabled' : ''}`}
              onClick={() => {
                if (!isAssigning) setNotify(!notify);
              }}
              style={{
                opacity: isAssigning ? 0.5 : 1,
                pointerEvents: isAssigning ? 'none' : 'auto',
              }}
            >
              <div className="notify-header">
                <Checkbox checked={notify} className="notify-checkbox" />
                <span className="notify-title">Notify client now</span>
              </div>
              <p
                className="notify-subtitle"
                title={`Send a notification to ${selectedClient ? `${selectedClient.name || ''} ${selectedClient.surname || ''}`.trim() : 'client name'}`}
              >
                Send a notification to{' '}
                {selectedClient
                  ? `${selectedClient.name || ''} ${selectedClient.surname || ''}`.trim()
                  : 'client name'}
              </p>
            </div>
          </div>
        </div>

        <div className="message-group">
          <label>
            <span>Add message (optional)</span>
            <span className="char-count">{message.length}/1000</span>
          </label>
          <div className="textarea-wrapper">
            <textarea
              placeholder="Type here..."
              maxLength={1000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={(e) => setMessage(e.target.value.trim())}
              disabled={isAssigning}
            />
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose} disabled={isAssigning}>
            Cancel
          </button>
          <button
            className="btn-assign-primary"
            onClick={handleAssign}
            disabled={!selectedClient || isAssigning}
          >
            {isAssigning && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 8, animation: 'custom-spin 1s linear infinite' }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
            {clientPrograms.length > 0 ? 'Replace Program' : 'Assign to client'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
